import {
  ILeakLOCChartProps,
  ILeakLOCResponseModel,
} from "../../system-performance/models/chart";
import { TimePeriod, eventType } from "../../../../../API";
import { useEffect, useMemo, useRef, useState } from "react";

import ApexOptions from "../../../../../common/components/apexchart/ApexChartModels";
import { DEFAULT_STATISTICS_DATE_FORMAT } from "../../system-health/common/values";
import { createChartTooltip } from "../../system-health/common/chartTooltip";
import dayjs from "dayjs";
import defaultApexOptions from "../../system-health/common/defaultApexOptions";
import { getAxisScale } from "../../../charts.helper";
import uniq from "lodash/uniq";
import { useGetStatisticsData } from "../../system-health/hooks/useGetStatisticsData";
import { useTheme } from "@mui/material";
import useLocationFromCache from "../../../../../common/hooks/useLocationFromCache";

export const useCreateLeakLOCChart = <T extends ILeakLOCResponseModel>(
  timePeriod: TimePeriod,
  locations: string[] | null,
  eventTypeActual: eventType
): ILeakLOCChartProps<T> => {
  const { loading, data, refetching, refetchChartData } = useGetStatisticsData<
    T[]
  >({
    eventType: eventTypeActual,
    models: [],
    locations,
    timePeriod,
  });

  const theme = useTheme();

  const options = defaultApexOptions(theme);

  const { getCachedLocationName } = useLocationFromCache();

  const [chartSeries, setChartProps] = useState<
    ApexAxisChartSeries | ApexNonAxisChartSeries | undefined
  >([]);

  const [chartOptions, setChartOptions] = useState<ApexOptions>(options);

  const individualSeriesDataRef = useRef<any[]>([]);

  useEffect(() => {
    const series = generateChartSeries(data);
    const extendedOptions = generateChartCategories(data);

    setChartProps(series);

    setChartOptions(extendedOptions);
  }, [data, theme.palette.mode]);

  const tooltip = useMemo(
    () => ({
      ...createChartTooltip({ percent: false }),
      custom: function ({
        _series,
        dataPointIndex,
        w,
      }: {
        _series: ApexAxisChartSeries;
        dataPointIndex: number;
        w: any;
      }) {
        const individualSeriesData = individualSeriesDataRef.current || [];

        const total = individualSeriesData.reduce((sum, series: any) => {
          const value = series.data[dataPointIndex];

          return sum + (value || 0);
        }, 0);

        const date = w.globals.categoryLabels[dataPointIndex];

        const hoverList = `
         <div class="apexcharts-tooltip-box">
            <div style="display: flex; justify-content: space-between; align-items: center;   gap: 4px;">
              <strong>Total:</strong>  <strong>${total}</strong>
            </div>

            <hr style="margin-top:-2px;">

            <div style="padding-top: 8px;">
              ${individualSeriesData
                .map((series: any) => {
                  const value = series.data[dataPointIndex];

                  if (value !== null && value !== undefined && value > 0) {
                    return `<div style="display: flex; justify-content: space-between; align-items: center; gap: 4px;"><span>${series.name}:</span> <strong>${value}</strong></div>`;
                  }

                  return "";
                })
                .join("")}
            </div>
          </div>  
        `;

        return `
          ${hoverList}
          <hr style="margin-top:-2px;">
          <div class="tooltip-date">${date}</div>
        `;
      },
    }),
    []
  );

  const generateChartSeries = (response: T[]) => {
    if (!Array.isArray(response) || response.length === 0) {
      return [];
    }

    if (timePeriod === "ONE_DAY") {
      const groupedByDate = response.reduce((acc, el) => {
        const dateField = (el as any).time;

        // Extract date part directly from the string to avoid timezone issues
        const dateKey = dateField.split(" ")[0]; // Gets "2025-07-31" directly

        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }

        acc[dateKey].push(el);

        return acc;
      }, {} as Record<string, T[]>);

      const labels = Object.keys(groupedByDate).sort();

      const aggregatedData = labels.map(dateKey => {
        const entriesForDate = groupedByDate[dateKey];
        const total = entriesForDate.reduce((sum, entry) => {
          if (entry && "event_count" in entry) {
            return sum + Math.round(parseFloat(entry.event_count ?? 0));
          }

          return sum;
        }, 0);

        return total;
      });

      const uniqueItemsIds = uniq(response.map(el => el.id));

      const individualSeriesData = uniqueItemsIds.map(itemId => {
        const seriesData = labels.map(dateKey => {
          const entriesForDate = groupedByDate[dateKey];

          const totalForItem = entriesForDate
            .filter(el => el.id === itemId)
            .reduce((sum, entry) => {
              if (entry && "event_count" in entry) {
                return sum + Math.round(parseFloat(entry.event_count ?? 0));
              }

              return sum;
            }, 0);

          return totalForItem;
        });

        const name = getCachedLocationName(itemId);

        return {
          name,
          data: seriesData,
        };
      });

      individualSeriesDataRef.current = individualSeriesData;

      return [
        {
          name: "Total",
          data: aggregatedData,
          type:
            aggregatedData.filter(val => val !== null).length === 1
              ? "scatter"
              : "line",
          color: "#FFA500",
        },
      ];
    } else {
      // For other time periods, use time field and group by appropriate date
      const groupedByDate = response.reduce((acc, el) => {
        const dateField = (el as any).time;

        // Validate the date field
        if (!dateField || typeof dateField !== "string") {
          console.warn("Invalid date field:", dateField);

          return acc;
        }

        // Extract date part directly from the string to avoid timezone issues
        const dateKey = dateField.split(" ")[0]; // Gets "2025-07-31" directly

        // Validate the date key format
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
          console.warn("Invalid date key format:", dateKey);

          return acc;
        }

        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }

        acc[dateKey].push(el);

        return acc;
      }, {} as Record<string, T[]>);

      const labels = Object.keys(groupedByDate).sort();

      const aggregatedData = labels.map(dateKey => {
        const entriesForDate = groupedByDate[dateKey];
        const total = entriesForDate.reduce((sum, entry) => {
          if (entry && "event_count" in entry) {
            return sum + Math.round(parseFloat(entry.event_count ?? 0));
          }

          return sum;
        }, 0);

        return total;
      });

      const uniqueItemsIds = uniq(response.map(el => el.id));

      const individualSeriesData = uniqueItemsIds.map(itemId => {
        const seriesData = labels.map(dateKey => {
          const entriesForDate = groupedByDate[dateKey];
          // Sum all events for this itemId on this date
          const totalForItem = entriesForDate
            .filter(el => el.id === itemId)
            .reduce((sum, entry) => {
              if (entry && "event_count" in entry) {
                return sum + Math.round(parseFloat(entry.event_count ?? 0));
              }

              return sum;
            }, 0);

          return totalForItem;
        });

        const name = getCachedLocationName(itemId);

        return {
          name,
          data: seriesData,
        };
      });

      individualSeriesDataRef.current = individualSeriesData;

      return [
        {
          name: "Total",
          data: aggregatedData,
          type:
            aggregatedData.filter(val => val !== null).length === 1
              ? "scatter"
              : "line",
          color: "#FFA500",
        },
      ];
    }
  };

  const generateChartCategories = (response: T[]): ApexOptions => {
    if (!Array.isArray(response) || response.length === 0) {
      return options;
    }

    let labels: string[];

    if (timePeriod === "ONE_DAY") {
      const groupedByDate = response.reduce((acc, el) => {
        const dateField = (el as any).time;

        // Extract date part directly from the string to avoid timezone issues
        const dateKey = dateField.split(" ")[0]; // Gets "2025-07-31" directly

        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }

        acc[dateKey].push(el);

        return acc;
      }, {} as Record<string, T[]>);

      labels = Object.keys(groupedByDate)
        .sort()
        .map(dateKey => dayjs(dateKey).format(DEFAULT_STATISTICS_DATE_FORMAT));
    } else {
      const groupedByDate = response.reduce((acc, el) => {
        const dateField = (el as any).time;

        if (!dateField || typeof dateField !== "string") {
          console.warn("Invalid date field:", dateField);

          return acc;
        }

        // Extract date part directly from the string to avoid timezone issues
        const dateKey = dateField.split(" ")[0]; // Gets "2025-07-31" directly

        // Validate the date key format
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
          console.warn("Invalid date key format:", dateKey);

          return acc;
        }

        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }

        acc[dateKey].push(el);

        return acc;
      }, {} as Record<string, T[]>);

      labels = Object.keys(groupedByDate)
        .sort()
        .map(dateKey => dayjs(dateKey).format(DEFAULT_STATISTICS_DATE_FORMAT));
    }

    const series = generateChartSeries(response);

    const totalCount = series.reduce((max, s) => {
      const maxInSeries = Math.max(...s.data);

      return Math.max(max, maxInSeries);
    }, 0);

    const { axisMax } = getAxisScale(totalCount);

    const extendedOptions: ApexOptions = {
      ...options,
      tooltip,
      stroke: {
        ...options.stroke,
        width: chartSeries?.length === 1 ? 4 : 2,
      },
      legend: {
        show: false,
      },
      yaxis: {
        ...options.yaxis,
        min: 0,
        max: axisMax,
        labels: {
          formatter: function (val) {
            const strValue = val.toString();

            const value = strValue.includes(".")
              ? (+strValue).toFixed(0)
              : strValue;

            return value;
          },
        },
      },
      xaxis: {
        type: "datetime",
        categories: labels,
        tickAmount: 14,
        tooltip: {
          enabled: false,
        },
        labels: {
          rotate: -70,
          showDuplicates: false,
          formatter: function (value: string) {
            return dayjs(value).format(DEFAULT_STATISTICS_DATE_FORMAT);
          },
        },
      },
    };

    return extendedOptions;
  };

  return {
    series: chartSeries,
    options: chartOptions,
    loading,
    originalData: data,
    refetching,
    showNoData: !loading && !refetching && data.length === 0,
    refetchChartData,
  };
};
